import React, { useEffect, useState } from 'react';
import { theme, message, Popconfirm } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { RootState } from 'store/configureStore';
import { clientReqOp } from 'store/ducks/ClientReq';
import {
  IClientReq,
  IPopoverState,
  ITextAreaBoxProps
} from 'store/ducks/ClientReq/types';
import Helpers from 'utils/Helpers';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import CustomTextarea from '../CustomTextarea';
import CustomIconButton from '../CustomIconButton';
import * as Styled from './TextAreaBox.styled';

const { useToken } = theme;

export const TextAreaBox: React.FC<ITextAreaBoxProps> = ({
  placeholder,
  aurReq,
  aurReqId,
  plusDisabled = false
}) => {
  const { token } = useToken();
  const dispatch = useDispatch();

  const { commentsData, clientReqs, lang } = useSelector(
    (state: RootState) => ({
      lang: state.global.lang,
      commentsData: state.complianceEst.commentsData,
      clientReqs: state.clientReq.clientReqs
    })
  );

  const [clnReqsToShow, setClnReqsToShow] = useState<{
    [key: number | string]: IClientReq;
  }>({});
  const [popoverState, setPopoverState] = useState<IPopoverState>(() => {
    const initialState: IPopoverState = {};

    !Helpers.isObjEmpty(aurReq.CLN_REQ) &&
      Object.values(aurReq.CLN_REQ).forEach((item: IClientReq) => {
        initialState[item.id as keyof typeof initialState] = false;
      });

    return initialState;
  });

  const hide = (id: number | string) => {
    if (!id) return;

    setPopoverState((prevState) => ({
      ...prevState,
      [id]: false
    }));
  };

  const handleOpenChange = (id: number | string) => {
    if (!id) return;

    setPopoverState((prevState) => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };

  const handleClientReqChange = (value: string, clnReqKey: string) => {
    const { aur_parent_key, aur_child_key } = aurReq;
    const originalRecord = clnReqsToShow[clnReqKey];

    const updatedRecord: IClientReq = {
      aur_parent_key,
      aur_child_key,
      value
    };

    if (originalRecord?.bru_id) {
      updatedRecord.bru_id = originalRecord.bru_id;
    } else if (originalRecord?.id) {
      updatedRecord.bru_id = originalRecord.id;
    }

    if (originalRecord?.category !== undefined) {
      updatedRecord.category = originalRecord.category;
    }

    if (originalRecord?.category_id !== undefined) {
      updatedRecord.category_id = originalRecord.category_id;
    }

    dispatch(clientReqOp.updateClientReq(updatedRecord, clnReqKey, aurReqId));
  };

  const handleDeleteClick = (id?: number | string) => {
    if (!id) return;

    dispatch(clientReqOp.deleteClientReq(id));
  };

  const handlePlusClick = () => {
    const newId = generateUniqueId();

    const { id, aur_parent_key, aur_child_key } = aurReq;

    const newClientReqElement = [
      {
        aurReqId: id,
        updatedAurReqs: {
          [newId]: {
            aur_parent_key,
            aur_child_key,
            value: '',
            bru_id: newId,
            category: undefined
          }
        }
      }
    ];

    dispatch(clientReqOp.setNewClientReqs(newClientReqElement));
  };

  const cancel = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    message.error('Evidence was not deleted');
  };

  // const drawComments = (id: number) => {
  //   const filteredComments = commentsData.length
  //     ? commentsData.filter(
  //         (commentsDataItem: CommentItem) =>
  //           commentsDataItem.res_table_pk_ref === id
  //       )
  //     : [];

  //   return (
  //     <>
  //       <CommentSection
  //         reqId={id}
  //         data={filteredComments}
  //         tableRef={CommentTable.BUSINESS_RULES}
  //       />
  //       <a onClick={() => hide(id)}>Close</a>
  //     </>
  //   );
  // };

  useEffect(() => {
    // Use the filtered CLN_REQ from aurReq instead of all clientReqs

    const clientReqData = clientReqs[aurReqId];
    if (!clientReqData) {
      setClnReqsToShow({});
      return;
    }

    const userDeselectedItems = JSON.parse(
      localStorage.getItem('userDeselectedCopyItems') || '[]'
    );
    const isUserDeselected = userDeselectedItems.includes(aurReqId);
    const hasOriginalClnReq =
      aurReq.CLN_REQ && Object.keys(aurReq.CLN_REQ).length > 0;
    const hasUpdatedReqs =
      clientReqData.updated_client_reqs &&
      Object.keys(clientReqData.updated_client_reqs).length > 0;

    if (isUserDeselected && !hasOriginalClnReq && !hasUpdatedReqs) {
      setClnReqsToShow({});
      return;
    }

    if (hasOriginalClnReq) {
      // Convert aurReq.CLN_REQ to the format expected by clnReqsToShow
      const filteredReqs: { [key: string]: IClientReq } = {};
      Object.entries(aurReq.CLN_REQ).forEach(([key, clnReq]) => {
        const clnReqId = clnReq.bru_id || clnReq.id || key;
        filteredReqs[clnReqId] = clnReq;
      });

      // Also include any updated client requirements for this aurReq if they exist and are not empty
      if (hasUpdatedReqs) {
        Object.entries(clientReqData.updated_client_reqs).forEach(
          ([key, updatedReq]) => {
            filteredReqs[key] = updatedReq;
          }
        );
      }

      setClnReqsToShow(filteredReqs);
    } else {
      // Fallback to original logic if no CLN_REQ in aurReq
      const { client_reqs, updated_client_reqs } = clientReqData;

      const combinedReqs = {
        ...client_reqs,
        ...(hasUpdatedReqs ? updated_client_reqs : {})
      };
      setClnReqsToShow(combinedReqs);
    }
  }, [aurReq.CLN_REQ, clientReqs, aurReqId]);

  return (
    <Styled.StyledContainer
      clnReqsExist={!Helpers.isObjEmpty(clnReqsToShow)}
      token={token}
    >
      {!Helpers.isObjEmpty(clnReqsToShow) ? (
        Object.keys(clnReqsToShow)
          .sort((a, b) => {
            // Sort by clnReq.id or bru_id to maintain consistent order
            const clnReqA = clnReqsToShow[a];
            const clnReqB = clnReqsToShow[b];
            const idA = clnReqA?.bru_id || clnReqA?.id || a;
            const idB = clnReqB?.bru_id || clnReqB?.id || b;
            return String(idA).localeCompare(String(idB));
          })
          .map((clnReqKey: string) => (
            <Styled.StyledDiv key={clnReqKey}>
              <Styled.TextAreaContainer>
                <CustomTextarea
                  placeholder={placeholder || 'Client Requirement'}
                  defaultValue={clnReqsToShow[clnReqKey].value}
                  onChange={(e) =>
                    handleClientReqChange(e.target.value, clnReqKey)
                  }
                  // autoSize
                />
              </Styled.TextAreaContainer>
              <Styled.StyledButtonSection token={token}>
                {/* Comment section hidden
              <Popover
                placement='leftBottom'
                trigger='click'
                content={drawComments(Number(clnReqKey))}
                title='Comments'
                open={!!(clnReqKey && popoverState[clnReqKey])}
                onOpenChange={() => handleOpenChange(clnReqKey)}
              >
                <CustomIconButton
                  type='primary'
                  shape='circle'
                  icon={<CommentOutlined />}
                  size='small'
                  cursor='pointer'
                />
              </Popover>
              */}
                <Popconfirm
                  title='Delete the client interpreted regulatory requirements'
                  description='Are you sure to delete this client interpreted regulatory requirements?'
                  onConfirm={() => handleDeleteClick(clnReqKey)}
                  onCancel={cancel}
                  okText='Yes'
                  cancelText='No'
                  overlayClassName='date-picker-white-bg'
                >
                  <Styled.StyledCustomIconButton
                    token={token}
                    type='primary'
                    shape='circle'
                    icon={<DeleteOutlined />}
                    cursor='pointer'
                    size='small'
                  />
                </Popconfirm>
              </Styled.StyledButtonSection>
            </Styled.StyledDiv>
          ))
      ) : (
        <Styled.Container>
          <Styled.StyledTextBox width={487} />
        </Styled.Container>
      )}
      <Styled.ButtonContainer>
        <CustomIconButton
          type='primary'
          shape='circle'
          icon={<PlusOutlined />}
          size='middle'
          onClick={handlePlusClick}
          disabled={plusDisabled}
        />
      </Styled.ButtonContainer>
    </Styled.StyledContainer>
  );
};
